import { AntdTable, AntTag, Progress } from '@agro-club/agroclub-shared'
import { Deal, DealType } from 'modules/domain/deal/types'
import { FC, useEffect, useState } from 'react'
import { useColumns } from './useColumns'
import { useTranslation } from 'react-i18next'
import formatPrice from 'modules/utils/numbers/formatPrice'
import { Card } from 'views/components/Card/Card'
import { useSingleEntity } from 'modules/domain/common/hooks'
import { endpoints } from 'modules/endpoints'
import { Spinner } from 'views/components/CommonTableComponents/CommonTableComponents'

type Props = { deal: Deal }

type LogistianData = {
  logistician_name: string
  timeout_amount: number
  lost_amount: number
  price_logistics_amount: number
}
type MarginContrbution = {
  logisticians: LogistianData[]
  regional: {
    regional_name?: string
    quality_amount: number
  }
}

type LogistianDisplayData = {
  rows: {
    title: string
    value: number
  }[]
  sum: number
  name: string
}

const transRoot = 'accordion.marginContribution'

export const MarginContribution: FC<Props> = ({ deal }) => {
  const columns = useColumns()
  const { t } = useTranslation('deal')
  const [dataForLogiscian, setDataForLogsician] = useState<LogistianDisplayData[]>([])
  const [dataForRegional, setDataForRegional] = useState<{ title: string; value: number }[]>([])
  const [progress, data, refetch] = useSingleEntity<MarginContrbution>(endpoints.marginContribution(deal.id))
  const isExportDeal = deal.deal_type === DealType.export
  useEffect(() => {
    if (!data) return
    const logData = data.logisticians.map((logistician) => {
      const rows = [
        { title: t(`${transRoot}.price_logistics_amount`), value: logistician.price_logistics_amount },
        { title: t(`${transRoot}.timeout_amount`), value: logistician.timeout_amount },
        { title: t(`${transRoot}.lost_amount`), value: logistician.lost_amount },
      ]
      const sum = logistician.price_logistics_amount + logistician.timeout_amount + logistician.lost_amount
      return { rows, sum, name: logistician.logistician_name }
    })
    setDataForLogsician(logData)
    setDataForRegional([{ title: t(`${transRoot}.quality_amount`), value: data.regional.quality_amount }])
  }, [data, deal.id, t])

  useEffect(() => {
    refetch()
  }, [deal, refetch])

  if (progress === Progress.WORK || !data) {
    return <Spinner />
  }

  return (
    <Card.GapableContent>
      {dataForLogiscian.map((logData) => (
        <Card.Container key={logData.name} bordered padding="compact">
          <Card.GapableContent>
            <div style={{ display: 'flex', gap: '4px' }}>
              <AntTag color="white">{logData.name}</AntTag>
              <AntTag color={logData.sum > 0 ? 'green' : 'red'}>{formatPrice(logData.sum, false, true)}</AntTag>
            </div>
            <AntdTable
              columns={columns}
              data={logData.rows}
              progress={Progress.SUCCESS}
              rowKey="title"
              outerBordered
              showHeader={false}
            />
          </Card.GapableContent>
        </Card.Container>
      ))}
      {dataForLogiscian.length == 0 && (
        <Card.Container bordered padding="compact">
          {t(`${transRoot}.noLogistianData`)}
        </Card.Container>
      )}
      {!isExportDeal && (
        <Card.Container bordered padding="compact">
          <Card.GapableContent>
            {data.regional.regional_name && (
              <div>
                <AntTag color="white">{data.regional.regional_name}</AntTag>
              </div>
            )}
            <AntdTable
              columns={columns}
              data={dataForRegional}
              progress={Progress.SUCCESS}
              rowKey="title"
              outerBordered
              showHeader={false}
            />
          </Card.GapableContent>
        </Card.Container>
      )}
    </Card.GapableContent>
  )
}
